import { useTerminology } from '@/lib/useTerminology'
import { Link } from 'react-router-dom'
import { erfuelltRecht } from '@symphenia/shared-types'
import { useAuthStore } from '@/lib/auth.store'
import { pageTitle, pageWide } from '@/lib/form-styles'
import { Folder, ListOrdered, Workflow, Tag, Users, Building, LayoutGrid, ListChecks, Columns3, ArrowRight, type LucideIcon } from 'lucide-react'

/**
 * „Auswahllisten" — Sammelseite für die Inhalte der Vorgabelisten.
 *
 * KA-46: Die Seite hängt an keinem Baustein mehr (vorher „Eigene Buchungsfelder" — ohne ihn gab es keinen
 * Weg zu den Betriebsstätten). Jede Karte erscheint, wenn Baustein **und** Recht ihrer Seite da sind.
 */
export default function SelectionListsPage() {
  // AP-16: Begriff des Mandanten (Projekt / Auftrag)
  const terms = useTerminology()
  const { user, hasModule } = useAuthStore()
  const darf = (recht: string, baustein?: string) => erfuelltRecht(recht, user?.permissions) && (!baustein || hasModule(baustein))
  const projekte = darf('time.project_ref.manage', 'TIME_TRACKING.PROJECT_TIME')
  const eigeneFelder = darf('time.selection.manage', 'TIME_TRACKING.CUSTOM_FIELDS')
  return (
    <div className={pageWide}>
      <div>
        <h1 className={pageTitle}>Auswahllisten</h1>
        <p className="mt-2 text-sm text-muted-foreground">Die Inhalte der Vorgabelisten, aus denen bei der Buchung gewählt wird.</p>
      </div>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {projekte && <LinkCard to="/time-tracking/projects" icon={Folder} title={terms.projectPlural} desc={`Vorgabeliste der ${terms.projectPlural}.`} />}
        {projekte && <LinkCard to="/time-tracking/positions" icon={ListOrdered} title={terms.positionPlural} desc={`Untergliederung der ${terms.projectPlural}.`} />}
        {projekte && <LinkCard to="/time-tracking/steps" icon={Workflow} title="Arbeitsschritte" desc={`Detailschritte je ${terms.position}.`} />}
        {projekte && <LinkCard to="/time-tracking/activity-types" icon={Tag} title="Tätigkeiten" desc="Katalog der Tätigkeitsarten." />}
        {darf('time.user_ref.manage') && <LinkCard to="/time-tracking/users" icon={Users} title="Mitarbeiter" desc="Mitarbeiter-Stammdaten." />}
        {darf('time.selection.manage') && <LinkCard to="/time-tracking/lists/betriebsstaette" icon={Building} title="Betriebsstätten" desc="Standorte / Betriebsstätten." />}
        {eigeneFelder && <LinkCard to="/time-tracking/lists/groups" icon={LayoutGrid} title="Zusatzfeld-Gruppen" desc="Vorgabedatensätze gruppierter Felder." />}
        {eigeneFelder && darf('time.config.manage') && <LinkCard to="/time-tracking/lists/field-options" icon={ListChecks} title="Auswahlwerte eigener Felder" desc="Werte der eigenen Dropdown-Felder zentral pflegen." />}
        {darf('time.ref_schema.manage', 'TIME_TRACKING.CUSTOM_FIELDS') && (
          <LinkCard to="/time-tracking/ref-schemas" icon={Columns3} title="Eigene Felder der Stammdaten" desc={`Zusatzfelder an ${terms.projectPlural}, Positionen, Tätigkeiten und Mitarbeitern.`} />
        )}
      </div>
    </div>
  )
}

function LinkCard({ to, icon: Icon, title, desc }: { to: string; icon: LucideIcon; title: string; desc: string }) {
  return (
    <Link
      to={to}
      className="flex flex-col gap-2 rounded-lg border border-border bg-background p-4 transition-colors hover:border-primary/50 hover:bg-muted/40"
    >
      <div className="flex items-center gap-2">
        <Icon className="h-5 w-5 flex-shrink-0 text-primary" />
        <span className="font-medium text-foreground">{title}</span>
        <ArrowRight className="ml-auto h-4 w-4 flex-shrink-0 text-muted-foreground" />
      </div>
      <span className="text-sm text-muted-foreground">{desc}</span>
    </Link>
  )
}
